import { getRequiredBaseUrl } from "@/utils/clients/baseUrl";
import { config } from "@/config";
import { logger } from "@/utils/logger/logger";
import type {
  ConsentDefinitionsResponse,
  RequirementStatus,
  ValidateRequirementsRequest,
} from "@/types/agreement";

export async function getConsentDefinitions(
  integrationName: string,
  lang?: string,
): Promise<ConsentDefinitionsResponse> {
  const integration = integrationName.trim();

  if (!integration) {
    throw new Error("Missing integration name for consent definitions");
  }

  const baseUrl = getRequiredBaseUrl(config.agreementBackendUrl, "agreement backend");
  const url = new URL(`${baseUrl}/agreements/${integration}/definitions/combined`);

  if (lang) url.searchParams.set("lang", lang);

  logger.info("[Agreement API] Fetching consent definitions", {
    integration,
    lang,
    url: url.toString(),
  });

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    logger.error("[Agreement API] Failed to fetch consent definitions", {
      integration,
      lang,
      url: url.toString(),
      status: response.status,
      statusText: response.statusText,
    });
    throw new Error(`Failed to fetch consent definitions: ${response.status}`);
  }

  const data = await response.json();
  logger.info("[Agreement API] Successfully fetched consent definitions", {
    integration,
    lang,
    consentsCount: data.consents?.length,
    termsCount: data.terms?.length,
  });
  return data;
}

export async function validateRequirements(
  integrationName: string,
  lang: string,
  accessToken: string,
  requirements: ValidateRequirementsRequest,
): Promise<RequirementStatus> {
  const integration = integrationName.trim();
  if (!integration) throw new Error("Missing integration name for requirements validation");

  const token = accessToken.trim();
  if (!token) throw new Error("Missing access token for requirements validation");

  const baseUrl = getRequiredBaseUrl(config.agreementBackendUrl, "agreement backend");
  console.log(baseUrl);
  const url = new URL(`${baseUrl}/agreements/${integration}/requirements`);
  url.searchParams.set("lang", lang);

  logger.info("[Agreement API] Validating requirements", {
    integration,
    lang,
    url: url.toString(),
    requirements,
  });

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requirements),
  });

  if (!response.ok) {
    logger.error("[Agreement API] Failed to validate requirements", {
      integration,
      lang,
      url: url.toString(),
      status: response.status,
      statusText: response.statusText,
    });
    throw new Error(`Failed to validate requirements: ${response.status}`);
  }

  const data = await response.json();
  logger.info("[Agreement API] Successfully validated requirements", {
    integration,
    lang,
    status: data,
  });
  return data;
}

export default getConsentDefinitions;
